// ────────────────────────────────────────────────────────────
// Wyoming API client wrappers
// ────────────────────────────────────────────────────────────
// Mirrors src/mi/services/aoiApi.ts + poolOrderApi.ts shape but for
// the Wyoming endpoints under functions/api/mi/wy/*.

import { authHeaders } from '../../lib/authHeaders';

export interface WyAoiListItem {
  id: string;
  name: string;
  basin: string | null;
  pinned_at: string;
  last_activity_at: string | null;
  party_count: number;
  trs_keys: string[];
}

export interface WyTrs {
  township_num: number;
  township_dir: 'N' | 'S';
  range_num: number;
  range_dir: 'E' | 'W';
  section: number;
}

export interface WyAoiMeta {
  id: string;
  name: string;
  basin: string | null;
  pinned_at: string;
  last_activity_at: string | null;
  notes: string | null;
  trs: WyTrs[];
}

export interface WyRosterRow {
  party_id: string;
  display_name: string;
  display_name_natural: string | null;
  normalized_name: string;
  entity_type: string;
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  skip_trace_city: string | null;
  skip_trace_state: string | null;
  ownership_rows_in_aoi: number;
  wi_rows_in_aoi: number;
  max_decimal_interest: number | null;
  has_phone: boolean;
  has_address: boolean;
}

export interface WyAoiDetailResponse {
  aoi: WyAoiMeta;
  roster: WyRosterRow[];
  counts: { all: number; working_interest: number; interested_party: number };
}

export interface WyPinAoiInput {
  name?: string;
  basin?: string | null;
  trs: Array<{
    township_num: number;
    township_dir: 'N' | 'S';
    range_num: number;
    range_dir: 'E' | 'W';
    section: number | string;
  }>;
}

export interface WyOrderListItem {
  id: string;
  order_number: string;
  operator: string | null;
  county: string | null;
  hearing_date: string | null;
  filed_date: string | null;
  parse_status: string;
  metadata_extracted_at: string | null;
  ownership_extracted_at: string | null;
  pdf_r2_key: string | null;
  ownership_count: number;
  wi_count: number;
  created_at: string;
}

export interface WyOwnershipRow {
  id: string;
  party_id: string;
  township_num: number;
  township_dir: 'N' | 'S';
  range_num: number;
  range_dir: 'E' | 'W';
  section: number;
  tract_description: string | null;
  record_class: 'interested_party' | 'working_interest';
  source: 'manual' | 'csv' | 'pdf_parse';
  interest_type: string | null;
  decimal_interest: number | null;
  interest_description_raw: string | null;
  created_by: string;
  created_at: string;
  display_name: string;
  display_name_natural: string | null;
  entity_type: string;
  skip_trace_city: string | null;
  skip_trace_state: string | null;
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  skip_traced_at: string | null;
  needs_review: boolean;
  deceased: boolean;
  enformion_candidates: unknown;
  enformion_tahoe_id: string | null;
  has_phone: boolean;
  has_address: boolean;
}

export interface WyOrderDetailResponse {
  wy_order: {
    id: string;
    order_number: string;
    operator: string | null;
    county: string | null;
    hearing_date: string | null;
    filed_date: string | null;
    pdf_r2_key: string | null;
    parse_status: string;
    parse_warnings: string[];
    metadata_extracted_at: string | null;
    ownership_extracted_at: string | null;
    notes: string | null;
    uploaded_by: string;
    created_at: string;
    trs: WyTrs[];
  };
  interested_parties: WyOwnershipRow[];
  working_interests:  WyOwnershipRow[];
  counts: { ownership_total: number; interested_party: number; working_interest: number };
}

export interface WyAddLeadBody {
  name: string;
  trs: WyTrs;
  record_class: 'interested_party' | 'working_interest';
  city?: string | null; state?: string | null;
  street?: string | null; zip?: string | null;
  tract_description?: string | null;
  interest_description?: string | null;
  interest_type_override?: string | null;
  decimal_interest_override?: number | null;
}

async function http<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch { /* fallthrough */ }
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

export const wyApi = {
  // AOIs
  listAois: () => http<{ aois: WyAoiListItem[] }>('/api/mi/wy/aois'),
  pinAoi:   (body: WyPinAoiInput) =>
    http<{ id: string; name: string; basin: string | null; trs_count: number }>(
      '/api/mi/wy/aois',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  getAoi:   (id: string) => http<WyAoiDetailResponse>(`/api/mi/wy/aois/${id}`),
  unpinAoi: (id: string) => http<{ ok: true }>(`/api/mi/wy/aois/${id}`, { method: 'DELETE' }),

  // Orders
  listOrders: () => http<{ orders: WyOrderListItem[] }>('/api/mi/wy/orders'),
  uploadOrder: async (form: FormData) => {
    const r = await fetch('/api/mi/wy/orders', {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: form,
    });
    if (!r.ok) {
      let detail = r.statusText;
      try { detail = (await r.json()).error ?? detail; } catch { /* fallthrough */ }
      throw new Error(`${r.status} ${detail}`);
    }
    return r.json() as Promise<{
      wy_order_id: string; order_number: string; parse_status: string;
      metadata_extracted_at: string | null; ownership_extracted_at: string | null;
      ownership_inserted: number; parse_warnings: string[];
    }>;
  },
  uploadCsv: async (csvText: string) => {
    const r = await fetch('/api/mi/wy/orders/csv', {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders(), 'Content-Type': 'text/csv' },
      body: csvText,
    });
    if (!r.ok) {
      let detail = r.statusText;
      try { detail = (await r.json()).error ?? detail; } catch { /* fallthrough */ }
      throw new Error(`${r.status} ${detail}`);
    }
    return r.json() as Promise<{
      inserted_ownership: number;
      parties_created: number;
      parties_reused: number;
      errors: Array<{ row_number: number; message: string }>;
    }>;
  },
  getOrder: (id: string) => http<WyOrderDetailResponse>(`/api/mi/wy/orders/${id}`),
  retryParse: (id: string) =>
    http<{
      wy_order_id: string; ownership_inserted: number;
      chunk_count: number; chunks_failed: number; chunk_warnings: string[];
    }>(`/api/mi/wy/orders/${id}/retry-parse`, { method: 'POST' }),
  addLead: (orderId: string, body: WyAddLeadBody) =>
    http<{
      wy_ownership_id: string; party_id: string;
      newly_created_party: boolean; record_class: string;
      interest_type: string | null; decimal_interest: number | null;
      interest_description_raw: string | null;
    }>(`/api/mi/wy/orders/${orderId}/add-lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
};
